import { useQuery } from '@tanstack/react-query';
import { adminProblemsApi, type AdminProblem } from '../api/admin-problems';
import { useAuthStore } from '@/stores/authStore';

/**
 * The caller's own unpublished problems, for the curriculum problem picker.
 *
 * The picker's main list reads the public catalogue, which by definition only
 * carries published problems — so without this a trainer could not attach a
 * problem they had just written. `/admin/problems` scopes its rows to the
 * caller server-side, so a trainer sees only their own drafts here.
 *
 * Disabled for students and org admins: they have no authoring surface, and the
 * endpoint would 403.
 */
export function useAuthoredDrafts(q: string, difficulty: string) {
  const role = useAuthStore(s => s.user?.role);
  const canAuthor = role === 'trainer' || role === 'super_admin';

  return useQuery({
    queryKey: ['authored-drafts', { q, difficulty }],
    enabled: canAuthor,
    staleTime: 60_000,
    queryFn: async (): Promise<AdminProblem[]> => {
      const data = await adminProblemsApi.list({
        status: 'draft',
        q: q || undefined,
        difficulty: difficulty || undefined,
        page: 1,
      });
      return data.problems ?? [];
    },
  });
}
